import { useLocation } from "react-router-dom";

export type EntityKind = "counterparty" | "broker";

export const MERGERS_PATH = "/mergers";

const STANDARD_BASE_PATHS: Record<EntityKind, string> = {
  counterparty: "/tradingcounterparties/counterparty",
  broker: "/brokers/broker"
};

/** Where an entity's detail page lives: mergers have their own tab, everything else its book's. */
export const entityBasePath = (kind: EntityKind, isMerger?: boolean) =>
  isMerger ? `${MERGERS_PATH}/${kind}` : STANDARD_BASE_PATHS[kind];

export const isMergersPath = (pathname: string) =>
  pathname === MERGERS_PATH || pathname.startsWith(`${MERGERS_PATH}/`);

/**
 * The path the entity should be on when the current one has the wrong prefix (a merger opened
 * under its book's tab, or a normal entity under /mergers, e.g. from an old link), else null.
 * Keeps everything after the base path, so a lifecycle or /signoff suffix survives the redirect.
 */
export const correctedEntityPath = (
  kind: EntityKind,
  pathname: string,
  isMerger?: boolean
): string | null => {
  const onMergers = isMergersPath(pathname);
  if (onMergers === Boolean(isMerger)) return null;
  const currentBase = entityBasePath(kind, onMergers);
  if (!pathname.startsWith(currentBase)) return null;
  return entityBasePath(kind, isMerger) + pathname.slice(currentBase.length);
};

/**
 * The counterparty and broker detail pages are mounted under their own book and under
 * /mergers. Anything that links back to the page (dialog close, sign-off button, route guards)
 * uses this, so it stays under whichever prefix the user is on and the nav keeps the right tab.
 */
export const useEntityBasePath = (kind: EntityKind) => {
  const { pathname } = useLocation();
  return entityBasePath(kind, isMergersPath(pathname));
};
